import { useRender, useTheme } from "@op/utils";
import { useMemo, type ComponentType } from "react";

export type LazyComponentProps<ComponentProps> = ComponentProps & {
  name: string;
};

// @ts-ignore Vite glob imports are resolved at build time
const components = import.meta.glob<{ default: ComponentType<any> }>(
  `../themes/*/*/components/*/*.tsx`,
  {
    eager: true,
  },
);

const NullComponent = ({ name }: { name: string }) => {
  return <code>Component {name} not found</code>;
};

const FALLBACK_RENDER_MEDIUM: "screen" | "print" = "screen";

const componentCache = new Map<string, ComponentType<any>>();

function getComponent(
  organizationSlug: string,
  projectSlug: string,
  defaultOrganizationSlug: string,
  defaultProjectSlug: string,
  name: string,
  renderMedium: "screen" | "print",
) {
  let componentKey = `../themes/${organizationSlug}/${projectSlug}/components/${renderMedium}/${name}.tsx`;
  let componentEntry = components[componentKey];
  if (!componentEntry) {
    componentKey = `../themes/${organizationSlug}/${projectSlug}/components/${FALLBACK_RENDER_MEDIUM}/${name}.tsx`;
    componentEntry = components[componentKey];
  }
  if (!componentEntry) {
    componentKey = `../themes/${defaultOrganizationSlug}/${defaultProjectSlug}/components/${renderMedium}/${name}.tsx`;
    componentEntry = components[componentKey];
  }
  if (!componentEntry) {
    componentKey = `../themes/${defaultOrganizationSlug}/${defaultProjectSlug}/components/${FALLBACK_RENDER_MEDIUM}/${name}.tsx`;
    componentEntry = components[componentKey];
  }
  if (!componentEntry) {
    if (import.meta.env.DEV) {
      console.warn(
        `No component found for name: ${name} in any theme or medium`,
      );
    }
    componentCache.set(componentKey, NullComponent);
  }
  if (!componentCache.has(componentKey)) {
    componentCache.set(componentKey, componentEntry.default);
  }
  return componentCache.get(componentKey)!;
}

export const LazyComponent = <Props,>({
  name,
  ...rest
}: LazyComponentProps<Props>) => {
  const {
    organizationSlug,
    projectSlug,
    defaultOrganizationSlug,
    defaultProjectSlug,
  } = useTheme();

  const { renderMedium } = useRender();

  const Component = useMemo(
    () =>
      getComponent(
        organizationSlug,
        projectSlug,
        defaultOrganizationSlug,
        defaultProjectSlug,
        name,
        renderMedium,
      ),
    [
      organizationSlug,
      projectSlug,
      defaultOrganizationSlug,
      defaultProjectSlug,
      name,
      renderMedium,
    ],
  );

  return <Component {...rest} />;
};
